import App from './App.vue';
import {
  createSSRApp,
  createApp as createVueApp,
  App as VueApp,
  defineAsyncComponent,
} from 'vue';
import type { AemData, TechDataVehicle, TechDataVariations } from './types';
import { loadVariations, loadVehicles } from './service/getVehicleService';
import { mapSSRContextToAemData } from './utils/aemData';

import {
  CabinTypeLabel,
  CabinVariations,
  EngineTypeLabel,
  EngineTypesLabel,
  DrivetrainLabel,
  DrivetrainTypesLabel,
  RoofTypeLabel,
  RoofVariations,
  SizeEngineVariations,
  SizeTypeLabel,
  SizeVariations,
} from './types';
import { fetchTranslations } from './service/i18n';
import { getVehicleType } from './utils/vehicleType';

const createApp = async (
  renderMode: string,
  componentId: string,
  data: any,
  error: { [x: string]: unknown } | null | undefined,
) => {
  let app: VueApp<Element>;

  if (!data || error) {
    console.log(
      'SSR error |',
      `Locale: ${data?.aemData.country}-${data?.aemData.language} |`,
      `Model: ${data?.aemData.modelSeries}`,
      `RenderMode: ${renderMode}`,
    );

    app = createVueApp(
      defineAsyncComponent(() => import('./AppError.vue')),
      error,
    );
  } else if (import.meta.env.SSR || renderMode !== 'csr') {
    console.log(
      'SSR request |',
      `Locale: ${data.aemData.country}-${data.aemData.language} |`,
      `Model: ${data.aemData.modelSeries}`,
      `RenderMode: ${renderMode}`,
    );
    app = createSSRApp(App, { componentId, data });
  } else {
    console.log(
      'SSR now working - Client used instead |',
      `Locale: ${data?.aemData.country}-${data?.aemData.language} |`,
      `Model: ${data?.aemData.modelSeries}`,
      `RenderMode: ${renderMode}`,
    );

    app = createVueApp(App, { componentId, data });
  }

  return app;
};

const loader = async (context: any, isSSR: boolean) => {
  const aemData = mapSSRContextToAemData(context, isSSR);

  let data: any = null;
  let translations: any = {};
  let processedData: any = { vehicles: [], variations: [] };

  try {
    data = await handleApiCalls(aemData);
  } catch (error) {
    console.error('Failed to fetch API data:', error);
    data = {};
  }

  try {
    translations = await fetchTranslations(aemData.language, aemData.country);
  } catch (error) {
    console.error('Failed to fetch translations:', error);
    translations = {};
  }

  try {
    const maybeProcessed = processAPIData(data || {});
    if (maybeProcessed) {
      processedData = maybeProcessed;
    } else {
      console.warn(
        'Processing API data returned null/undefined, using fallback.',
      );
    }
  } catch (error) {
    console.error('Error processing API data:', error);
  }

  // Calculate selected variant
  const selectedVariant = selectInitialVariant(
    processedData.vehicles,
    processedData.variations,
  );

  return {
    aemData,
    processedData,
    translations,
    selectedVariant,
  };
};

const selectInitialVariant = (
  vehicles: TechDataVehicle[],
  variations: TechDataVariations,
): TechDataVehicle | undefined => {
  const vehicleType = getVehicleType(variations);
  const firstVariation = variations.variations?.[0];

  if (!firstVariation) {
    console.warn('No variations available');
    return vehicles[0];
  }

  try {
    if (vehicleType === 'Citan') {
      const firstEngine = (firstVariation as SizeEngineVariations)
        .engineTypes[0].engineType;
      return vehicles.find(
        (e) =>
          e.sizeType === firstVariation.sizeType &&
          e.engineType === firstEngine,
      );
    }

    if (vehicleType === 'Vito') {
      const firstWeight = (firstVariation as SizeVariations).weightsMax[0]
        .weightMax;
      const firstDrivetrain = (firstVariation as SizeVariations).weightsMax[0]
        .drivetrainTypes[0].drivetrainType;
      const firstEngine = (firstVariation as SizeVariations).weightsMax[0]
        .drivetrainTypes[0].engineTypes[0].engineType;
      return vehicles.find(
        (e) =>
          e.sizeType === firstVariation.sizeType &&
          e.weights?.weightMax?.value === firstWeight &&
          e.drivetrainType === firstDrivetrain &&
          e.engineType === firstEngine,
      );
    }

    if (vehicleType === 'Sprinter_Cabin') {
      const firstCabin = (firstVariation as CabinVariations).cabinTypes[0]
        .cabinType;
      const firstWeight = (firstVariation as CabinVariations).cabinTypes[0]
        .weightsMax[0].weightMax;
      const firstDrivetrain = (firstVariation as CabinVariations).cabinTypes[0]
        .weightsMax[0].drivetrainTypes[0].drivetrainType;
      const firstEngine = (firstVariation as CabinVariations).cabinTypes[0]
        .weightsMax[0].drivetrainTypes[0].engineTypes[0].engineType;
      return vehicles.find(
        (e) =>
          e.sizeType === firstVariation.sizeType &&
          e.cabinType === firstCabin &&
          e.weights?.weightMax?.value === firstWeight &&
          e.drivetrainType === firstDrivetrain &&
          e.engineType === firstEngine,
      );
    }

    if (vehicleType === 'Sprinter_Roof') {
      const firstRoof = (firstVariation as RoofVariations).roofHeightTypes[0]
        .roofHeightType;
      const firstWeight = (firstVariation as RoofVariations).roofHeightTypes[0]
        .weightsMax[0].weightMax;
      const firstDrivetrain = (firstVariation as RoofVariations)
        .roofHeightTypes[0].weightsMax[0].drivetrainTypes[0].drivetrainType;
      const firstEngine = (firstVariation as RoofVariations).roofHeightTypes[0]
        .weightsMax[0].drivetrainTypes[0].engineTypes[0].engineType;
      return vehicles.find(
        (e) =>
          e.sizeType === firstVariation.sizeType &&
          e.roofHeightType === firstRoof &&
          e.weights?.weightMax?.value === firstWeight &&
          e.drivetrainType === firstDrivetrain &&
          e.engineType === firstEngine,
      );
    }
  } catch (error) {
    console.warn('Error selecting initial variant:', error);
  }

  return vehicles[0]; // Fallback to first vehicle if no match
};

const getVehicleData = (response: any) => {
  if (!(response instanceof Error)) {
    const variations = response.variations;

    const drivetrainsSet = new Set<DrivetrainLabel>();
    const engineTypesSet = new Set<EngineTypeLabel>();
    const batteryCapacitiesSet = new Set<number>();
    let weightsMax: number[] = [];
    let roofTypes: RoofTypeLabel[] = [];
    if ((variations[0] as RoofVariations).roofHeightTypes) {
      // extract values
      (variations as RoofVariations[]).forEach((roofVariant) => {
        roofTypes.push(
          ...roofVariant.roofHeightTypes.map((e) => e.roofHeightType),
        );
        roofVariant.roofHeightTypes.map((roofType) => {
          weightsMax.push(...roofType.weightsMax.map((e) => e.weightMax));

          roofType.weightsMax.map((e) => {
            e.drivetrainTypes.map((e: DrivetrainTypesLabel) => {
              drivetrainsSet.add(e.drivetrainType);
              e.engineTypes.map((e: EngineTypesLabel) => {
                engineTypesSet.add(e.engineType);
                Array.isArray(e.batteryCapacities) &&
                  e.batteryCapacities.forEach((e) => {
                    batteryCapacitiesSet.add(e);
                  });
              });
            });
          });
        });
      });
      // remove duplicates and sort
      roofTypes = Array.from(
        new Set(roofTypes.sort((e) => Object.keys(RoofTypeLabel).indexOf(e))),
      );
    }

    let cabinTypes: CabinTypeLabel[] = [];
    if ((variations[0] as CabinVariations).cabinTypes) {
      // extract values
      (variations as CabinVariations[]).forEach((cabinVariant) => {
        cabinTypes.push(...cabinVariant.cabinTypes.map((e) => e.cabinType));
        cabinVariant.cabinTypes.map((cabinType) => {
          weightsMax.push(...cabinType.weightsMax.map((e) => e.weightMax));

          cabinType.weightsMax.map((e) => {
            e.drivetrainTypes.map((e: DrivetrainTypesLabel) => {
              drivetrainsSet.add(e.drivetrainType);
              e.engineTypes.map((e: EngineTypesLabel) => {
                engineTypesSet.add(e.engineType);
                Array.isArray(e.batteryCapacities) &&
                  e.batteryCapacities.forEach((e) => {
                    batteryCapacitiesSet.add(e);
                  });
              });
            });
          });
        });
      });
      // remove duplicates and sort
      cabinTypes = Array.from(
        new Set(cabinTypes.sort((e) => Object.keys(CabinTypeLabel).indexOf(e))),
      );
    }

    if ((variations[0] as SizeEngineVariations).engineTypes) {
      // extract values
      (variations as SizeEngineVariations[]).forEach((sizeEngineVariant) => {
        sizeEngineVariant.engineTypes.map((e) => {
          engineTypesSet.add(e.engineType);
          Array.isArray(e.batteryCapacities) &&
            e.batteryCapacities.forEach((e) => {
              batteryCapacitiesSet.add(e);
            });
        });
      });
    }

    if ((variations[0] as SizeVariations).weightsMax) {
      (variations as SizeVariations[]).forEach((sizeVariant) => {
        weightsMax.push(...sizeVariant.weightsMax.map((e) => e.weightMax));
        sizeVariant.weightsMax.map((e) => {
          e.drivetrainTypes.map((e: DrivetrainTypesLabel) => {
            drivetrainsSet.add(e.drivetrainType);
            e.engineTypes.map((e: EngineTypesLabel) => {
              engineTypesSet.add(e.engineType);
              Array.isArray(e.batteryCapacities) &&
                e.batteryCapacities.forEach((e) => {
                  batteryCapacitiesSet.add(e);
                });
            });
          });
        });
      });
    }

    // remove duplicates and sort
    weightsMax = Array.from(new Set(weightsMax.sort()));
    const drivetrain = Array.from(drivetrainsSet);
    const engineTypes = Array.from(engineTypesSet);

    const sizes: SizeTypeLabel[] = Array.from(
      new Set(
        variations
          .map((it: { sizeType: any }) => it.sizeType)
          .sort((e: string) => Object.keys(SizeTypeLabel).indexOf(e)),
      ),
    );

    const batteryCapacities = Array.from(batteryCapacitiesSet).sort();

    return {
      sizes,
      roofTypes,
      cabinTypes,
      drivetrain,
      engineTypes,
      batteryCapacities,
      weightsMax,
      response,
    };
  }
};

const processAPIData = (data: [TechDataVehicle[], TechDataVariations, any]) => {
  const [vehiclesResponse, variationsResponse] = data;

  const vehicleData = getVehicleData(variationsResponse);

  if (!(vehiclesResponse instanceof Error) && vehiclesResponse) {
    const vehicles = vehiclesResponse;
    const variations = !(variationsResponse instanceof Error)
      ? vehicleData?.response
      : null;

    return {
      vehicles,
      variations,
      sizes: vehicleData?.sizes || [],
      roofs: vehicleData?.roofTypes || [],
      cabins: vehicleData?.cabinTypes || [],
      batteryCapacities: vehicleData?.batteryCapacities || [],
      engines: vehicleData?.engineTypes || [],
      weights: vehicleData?.weightsMax || [],
      drivetrains: vehicleData?.drivetrain || [],
    };
  }

  return null;
};

const handleApiCalls = async (aemData: AemData) => {
  return Promise.all([loadVehicles(aemData), loadVariations(aemData)]);
};

export default {
  createApp,
  loader,
};
